/**
 * B169 — the boot classification. §9.2 resolution answers WHAT the URL
 * said; this answers whether the page can do anything with it. A hosted
 * storefront opened bare, or with only a channel site key, has no business
 * behind it: there is nothing to ask for and nothing to show. That is not
 * a failure and it is not an empty catalog — it is a page that has not
 * been connected yet, and it deserves its own face.
 *
 * Deciding this BEFORE the first call is what keeps the two lies of B169
 * off the screen: the app never asks (so no refusal banner) and never
 * lands in an empty product list (so no "nothing is on sale" about a
 * catalog full of tickets). Pure — the caller supplies the baked env
 * fallback rather than this module reading import.meta.
 */

import type { StorefrontContext } from "./context.js";
import { GUEST_DOOR } from "./refusals.js";

export type UnconnectedReason =
  /** Nothing in the URL and nothing baked: the page does not know whose it is. */
  | "no-context"
  /** A channel site key with no tenant — the browser has no way to resolve one. */
  | "site-key-unresolved";

export type BootState =
  | { readonly kind: "connected"; readonly tenantId: string }
  | { readonly kind: "unconnected"; readonly reason: UnconnectedReason };

export interface BootInput {
  readonly context: Pick<StorefrontContext, "tenantId" | "siteKey">;
  /** VITE_TENANT_ID as baked into this bundle, if any. */
  readonly envTenantId?: string;
}

/** A blank or whitespace-only id is not an id — the "" that produced B169. */
function id(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

export function classifyBoot(input: BootInput): BootState {
  const tenantId = id(input.context.tenantId) ?? id(input.envTenantId);
  if (tenantId !== undefined) return { kind: "connected", tenantId };
  return {
    kind: "unconnected",
    reason: id(input.context.siteKey) !== undefined ? "site-key-unresolved" : "no-context",
  };
}

/** Which face the boot region shows, and whether a notice may ride along. */
/** B1013 — "door": the guest door refused, so the page has nothing to show behind its sentence. */
export type BootFaceKind = "unconnected" | "loading" | "catalog" | "door";

export interface BootFace {
  readonly face: BootFaceKind;
  readonly showNotice: boolean;
}

export interface BootFaceInput {
  readonly boot: BootState;
  readonly catalogLoading: boolean;
  readonly notice?: string;
}

/**
 * THE ONE-FACE LAW (B169(a)). An unconnected boot has exactly one face and
 * no notice can stack on it: there is nothing loading, nothing empty and
 * nothing that failed. A connected boot keeps its ordinary faces, notices
 * included — this rule must not swallow the refusals buyers need to see.
 */
export function bootFace(input: BootFaceInput): BootFace {
  if (input.boot.kind !== "connected") return { face: "unconnected", showNotice: false };
  if (input.notice === GUEST_DOOR) return { face: "door", showNotice: false };
  return {
    face: input.catalogLoading ? "loading" : "catalog",
    showNotice: input.notice !== undefined,
  };
}

/* ------------------------------------------------- B181 siteKey resolve */

export interface ResolvedChannel {
  readonly tenantId: string;
  readonly channelId: string;
}

/**
 * B181 (RULED 2026-09-21) — the tenantless public call the keyed boot
 * rides: POST /ops/channel.resolve with NO tenant header (the caller has
 * none — that is the door's whole design). Misses, refusals and transport
 * failures all answer undefined: the boot then keeps B169's teaching
 * face, which is the honest floor for a page that cannot learn whose it
 * is. Never throws into the boot.
 */
export async function resolveSiteKey(
  apiBaseUrl: string,
  siteKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ResolvedChannel | undefined> {
  try {
    const res = await fetchImpl(`${apiBaseUrl.replace(/\/$/, "")}/ops/channel.resolve`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ siteKey }),
    });
    if (!res.ok) return undefined;
    const body = (await res.json()) as { channel?: { tenantId?: unknown; channelId?: unknown } };
    const channel = body.channel;
    if (channel === undefined || typeof channel.tenantId !== "string" || typeof channel.channelId !== "string") {
      return undefined;
    }
    return { tenantId: channel.tenantId, channelId: channel.channelId };
  } catch {
    return undefined;
  }
}

/** Exactly the unconnected-with-a-key boot asks the resolve — nothing else. */
export function needsSiteKeyResolve(boot: BootState): boolean {
  return boot.kind === "unconnected" && boot.reason === "site-key-unresolved";
}

/** The answered channel completes the context: tenant AND channel. */
export function mergeResolvedChannel(ctx: StorefrontContext, channel: ResolvedChannel): StorefrontContext {
  return { ...ctx, tenantId: channel.tenantId, channelId: channel.channelId };
}
